import { useQuery } from '@tanstack/react-query';
import type {
  InventoryStatusDto,
  ItemDto,
  LocationDto,
  Page,
  ReasonCodeDto,
} from '@zenarty/shared';
import { useEffect, useState } from 'react';
import { api } from '../api';

function useDebounced(value: string, ms = 250) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

/** Type-ahead over items (SKU, description, GTIN). */
export function ItemPicker({
  value,
  onChange,
  clientId,
  placeholder = 'Search SKU or description',
}: {
  value: ItemDto | null;
  onChange: (item: ItemDto | null) => void;
  clientId?: string;
  placeholder?: string;
}) {
  const [text, setText] = useState(value ? `${value.sku} — ${value.description}` : '');
  const [open, setOpen] = useState(false);
  const q = useDebounced(text);
  const results = useQuery({
    queryKey: ['items', 'pick', q, clientId],
    queryFn: () =>
      api<Page<ItemDto>>(
        `/items?limit=8&status=active&q=${encodeURIComponent(q)}${clientId ? `&clientId=${clientId}` : ''}`,
      ),
    enabled: open && q.length > 0,
  });
  return (
    <div style={{ position: 'relative' }}>
      <input
        className="input"
        value={text}
        placeholder={placeholder}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onChange={(e) => {
          setText(e.target.value);
          setOpen(true);
          if (value) onChange(null);
        }}
      />
      {open && results.data?.items.length ? (
        <div
          className="menu"
          style={{ left: 0, right: 0, top: 'calc(100% + 4px)', maxHeight: 260, overflow: 'auto' }}
        >
          {results.data.items.map((i) => (
            <button
              key={i.id}
              type="button"
              className="menu-item"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                onChange(i);
                setText(`${i.sku} — ${i.description}`);
                setOpen(false);
              }}
            >
              <span className="mono small">{i.sku}</span>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {i.description}
              </span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/** Type-ahead over location codes in one site. */
export function LocationPicker({
  siteId,
  value,
  onChange,
}: {
  siteId: string;
  value: LocationDto | null;
  onChange: (l: LocationDto | null) => void;
}) {
  const [text, setText] = useState(value?.code ?? '');
  const [open, setOpen] = useState(false);
  const q = useDebounced(text);
  const results = useQuery({
    queryKey: ['locations', 'pick', siteId, q],
    queryFn: () =>
      api<Page<LocationDto>>(
        `/locations?siteId=${siteId}&status=active&limit=10&q=${encodeURIComponent(q)}`,
      ),
    enabled: open && Boolean(siteId),
  });
  return (
    <div style={{ position: 'relative' }}>
      <input
        className="input mono"
        value={text}
        placeholder="Scan or type a location"
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onChange={(e) => {
          setText(e.target.value.toUpperCase());
          setOpen(true);
          if (value) onChange(null);
        }}
      />
      {open && results.data?.items.length ? (
        <div
          className="menu"
          style={{ left: 0, right: 0, top: 'calc(100% + 4px)', maxHeight: 260, overflow: 'auto' }}
        >
          {results.data.items.map((l) => (
            <button
              key={l.id}
              type="button"
              className="menu-item"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                onChange(l);
                setText(l.code);
                setOpen(false);
              }}
            >
              <span className="mono">{l.code}</span>
              <span className="muted small">
                {l.type} · {l.temperatureZone}
                {l.pickable ? ' · pick face' : ''}
              </span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export const useReasons = () =>
  useQuery({
    queryKey: ['reasons'],
    queryFn: () => api<ReasonCodeDto[]>('/reason-codes'),
    staleTime: 300_000,
  });
export const useStatuses = () =>
  useQuery({
    queryKey: ['statuses'],
    queryFn: () => api<InventoryStatusDto[]>('/inventory-statuses'),
    staleTime: 300_000,
  });

export function ReasonSelect({
  categories,
  value,
  onChange,
}: {
  categories: string[];
  value: string;
  onChange: (id: string) => void;
}) {
  const reasons = useReasons();
  const options = (reasons.data ?? []).filter((r) => r.active && categories.includes(r.category));
  return (
    <select className="select" value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">Choose a reason…</option>
      {options.map((r) => (
        <option key={r.id} value={r.id}>
          {r.name}
          {r.requiresNote ? ' (note required)' : ''}
        </option>
      ))}
    </select>
  );
}
